import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const HtmlBlogEditor = forwardRef(({ value, onChange, readOnly = false }, ref) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const iframeRef = useRef(null);

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const handlePreview = () => {
    const raw = value || '';

    if (!raw || !/<!DOCTYPE|<html|<head|<body/i.test(raw)) {
      setPreviewHtml(raw);
      setPreviewOpen(true);
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, 'text/html');

      doc.querySelectorAll('script, noscript').forEach((el) => el.remove());

      const serializeAttrs = (el) => {
        if (!el) return '';
        const names = el.getAttributeNames();
        if (names.length === 0) return '';
        return ' ' + names.map((n) => `${n}="${el.getAttribute(n).replace(/"/g, '&quot;')}"`).join(' ');
      };

      const htmlAttrs = serializeAttrs(doc.documentElement);
      const headContent = doc.head ? doc.head.innerHTML : '';
      const bodyAttrs = serializeAttrs(doc.body);
      const bodyContent = doc.body ? doc.body.innerHTML : raw;

      setPreviewHtml(`<!DOCTYPE html><html${htmlAttrs}><head>${headContent}</head><body${bodyAttrs}>${bodyContent}</body></html>`);
      setPreviewOpen(true);
    } catch (e) {
      console.warn('Preview parsing failed:', e);
      setPreviewHtml(raw);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewHtml('');
  };

  useImperativeHandle(ref, () => ({
    showPreview: handlePreview,
  }));

  return (
    <Box>
      <textarea
        value={value || ''}
        onChange={handleChange}
        readOnly={readOnly}
        style={{
          width: '100%',
          minHeight: '300px',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          padding: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxWidth: '1200px' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" fontWeight={600}>Preview</Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex' }}>
          <iframe
            ref={iframeRef}
            title="HTML Preview"
            srcDoc={previewHtml}
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-same-origin"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default HtmlBlogEditor;
